import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Award } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card.js';
import { Badge } from '../../components/ui/Badge.js';
import { SearchInput } from '../../components/ui/Input.js';
import { TableSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchAllEntitlements } from '../../api/system.js';
import { formatDate } from '../../lib/utils.js';

export function EntitlementsPage() {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { t } = useI18n();

  const { data: entitlements, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-entitlements'],
    queryFn: fetchAllEntitlements,
  });

  const filteredEntitlements = (entitlements || []).filter((e) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return e.user_id.toLowerCase().includes(term) || e.entitlement.toLowerCase().includes(term);
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-500" />
            {t('entitlements.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('entitlements.description')}
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="w-full sm:w-80">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('entitlements.searchPlaceholder')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={4} cols={5} />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState title={t('entitlements.loadError')} message={(error as any).message} onRetry={() => refetch()} />
            </div>
          ) : filteredEntitlements.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={t('entitlements.emptyTitle')}
                description={t('entitlements.emptyDesc')}
              />
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">{t('entitlements.tableUserId')}</th>
                  <th className="py-3 px-4">{t('entitlements.tableEntitlement')}</th>
                  <th className="py-3 px-4">{t('entitlements.tableStatus')}</th>
                  <th className="py-3 px-4">{t('entitlements.tableGrantedBy')}</th>
                  <th className="py-3 px-4">{t('entitlements.tableExpiresAt')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredEntitlements.map((ent) => (
                  <tr key={ent.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-3 px-4">
                      <button
                        onClick={() => navigate(`/admin/users/${ent.user_id}`)}
                        className="font-mono text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                      >
                        {ent.user_id}
                      </button>
                    </td>
                    <td className="py-3 px-4 font-mono font-bold text-slate-800 dark:text-slate-200">
                      {ent.entitlement}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={ent.status === 'active' ? 'success' : 'neutral'}>
                        {ent.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 uppercase text-[11px] text-slate-500 font-medium">
                      {ent.source}
                    </td>
                    <td className="py-3 px-4 text-slate-500">
                      {ent.expires_at ? formatDate(ent.expires_at) : 'Lifetime'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
